import React, { memo, useMemo, useCallback } from 'react';
import { NavItem, NavLink, Nav } from 'reactstrap';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNewspaper,
  faUsers,
  faIdCard,
  faThList,
  faCog
} from '@fortawesome/free-solid-svg-icons';

//Redux-actions
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { applicationSetting } from '../../../redux/actions';

//Helper
import strings from '../../../globalization';
import './Navigation.css';
import consts from '../../../consts';
const { UserRoleId } = consts;

const currentYear = new Date().getFullYear();

/**
 * @typedef {Object} MenuChild
 * @property {string} title - Display text for the child menu
 * @property {string} target - Route path that the child links to
 * @property {*} [icon] - Icon definition (FontAwesome)
 */

/**
 * @typedef {Object} Menu
 * @property {string} title - Display text for the menu
 * @property {string} [target] - Route path for leaf menus
 * @property {*} [icon] - Icon definition (FontAwesome)
 * @property {MenuChild[]} [children] - Sub-menu items
 */

/**
 * Static menu configuration.
 * @type {Menu[]}
 */
const menus = [
  {
    title: strings.ManageLicense,
    target: 'managelicense',
    icon: faIdCard
  },
  {
    title: strings.Configurations,
    icon: faCog,
    children: [
      {
        title: strings.EquipmentModel,
        target: 'modelList',
        icon: faThList
      },
      {
        title: strings.ManageUser,
        target: 'usermanagement',
        icon: faUsers
      },
      {
        title: strings.TrackingSystem,
        target: 'trackingSystem',
        icon: faThList
      },
      {
        title: strings.CatSoftwareVersion,
        target: 'catsoftwareversion',
        icon: faThList
      }
    ]
  },
  {
    title: strings.Reports,
    target: 'reports',
    icon: faNewspaper
  }
];

/**
 * Map of user role IDs to the list of allowed menu targets for that role.
 * @type {Record<number, string[]>}
 */
const roleMenuMapper = {
  [UserRoleId.SuperAdmin]: [
    'managelicense',
    'modelList',
    'usermanagement',
    'trackingSystem',
    'reports',
    'catsoftwareversion'
  ],
  [UserRoleId.Specialist]: [
    'managelicense',
    'modelList',
    'trackingSystem',
    'reports'
  ],
  [UserRoleId.Viewer]: ['managelicense', 'reports']
};

/**
 * Filter a menu tree to only include menus and children that are present in allowedTargets.
 *
 * - Parent menus are kept only if at least one child is allowed.
 * - Leaf menus are kept only if their target exists in allowedTargets.
 *
 * @param {Menu[]} menus - Full menu tree to filter
 * @param {string[]} allowedTargets - Array of allowed route targets for the current role
 * @returns {Menu[]} Filtered menu tree
 */
function filterMenusByRole(menus, allowedTargets) {
  return menus
    .map(menu => {
      if (menu.children) {
        const filteredChildren = menu.children.filter(child =>
          allowedTargets.includes(child.target)
        );
        if (!filteredChildren.length) {
          return null;
        }
        return { ...menu, children: filteredChildren };
      }

      return allowedTargets.includes(menu.target) ? menu : null;
    })
    .filter(Boolean);
}

/**
 * Menu item renderer used by the sidebar.
 *
 * Renders either:
 * - a parent menu with expandable children, or
 * - a leaf menu item that links to a route.
 *
 * Props:
 * - menu: Menu object
 * - index: Array index (used as key)
 * - selectedMenu: currently selected route target string
 * - onNavClick: callback invoked when a menu link is clicked
 *
 * @param {{menu: Menu, index: number, selectedMenu: string, onNavClick: (target: string) => void}} props
 * @returns {JSX.Element}
 */
const MenuItem = memo(({ menu, index, selectedMenu, onNavClick }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Parent menu with children
  if (menu.children) {
    return (
      <NavItem key={index} className="menu-parent">
        <div
          className="d-flex align-items-center px-3 py-2 menu-parent-toggle"
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: 'pointer' }}
        >
          <FontAwesomeIcon icon={menu.icon} className="mr-2" />
          {menu.title}
          <span className="ml-auto">{isOpen ? '▾' : '▸'}</span>
        </div>

        {isOpen && (
          <Nav vertical className="ml-3 list-unstyled">
            {menu.children.map((child, childIndex) => (
              <NavItem key={childIndex}>
                <NavLink
                  tag={Link}
                  to={`/${child.target}`}
                  onClick={() => onNavClick(child.target)}
                  active={
                    selectedMenu === child.target ||
                    window.location.hash
                      .toLowerCase()
                      .split('#')[1]
                      ?.includes(child.target)
                  }
                >
                  <FontAwesomeIcon icon={child.icon} className="mr-2" />
                  {child.title}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        )}
      </NavItem>
    );
  }

  return (
    <NavItem key={index}>
      <NavLink
        tag={Link}
        to={`/${menu.target}`}
        onClick={() => onNavClick(menu.target)}
        active={
          selectedMenu === menu.target ||
          window.location.hash
            .toLowerCase()
            .split('#')[1]
            ?.includes(menu.target)
        }
      >
        <FontAwesomeIcon icon={menu.icon} className="mr-2" />
        {menu.title}
      </NavLink>
    </NavItem>
  );
});

/**
 * Sidebar NavigationBar component.
 *
 * Determines which menus to display based on the current user's role,
 * reads settings from Redux, and dispatches menu selection updates.
 *
 * @returns {JSX.Element}
 */
const NavigationBar = memo(() => {
  const dispatch = useDispatch();
  const data = useSelector(state => {
    const { appVersion, userDetail } = state.loginData.data || {};
    const { isDark, navOpen, selectedMenu = '' } = state.settingData;
    return { appVersion, userDetail, isDark, navOpen, selectedMenu };
  }, shallowEqual);

  const { isDark, navOpen, appVersion, selectedMenu, userDetail } = data || {};

  const onNavClick = useCallback(
    target => {
      dispatch(
        applicationSetting({
          selectedMenu: `/${target}`
        })
      );
    },
    [dispatch]
  );

  const menuList = useMemo(() => {
    const allowedTargets =
      roleMenuMapper[userDetail && userDetail.UserRole] || [];
    return filterMenusByRole(menus, allowedTargets);
  }, [userDetail]);

  return (
    <div
      className={classNames(
        'sidebar',
        { 'is-open': navOpen },
        { 'sidebar-dark': isDark }
      )}
    >
      <div className="sidebar-header">
        <h3>
          <img src="./img/applogo.png" alt="Steris" className="ml-auto" />
        </h3>
      </div>
      <div className="side-menu">
        <Nav vertical className="list-unstyled pb-3">
          {menuList.map((menu, index) => (
            <MenuItem
              key={index}
              menu={menu}
              index={index}
              selectedMenu={selectedMenu}
              onNavClick={onNavClick}
            />
          ))}
        </Nav>
        <div className="footer-Tab">
          <div style={{ paddingTop: '2em' }}>
            {strings.CAT} v{appVersion}
          </div>
          <div style={{ paddingTop: '0.2em' }}>
            {strings.Copyright} &copy; {strings.SterisCorporation} {currentYear}
          </div>
        </div>
      </div>
    </div>
  );
});

export default NavigationBar;
